import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
// Removed react-webcam for more reliable direct video control
import { BrowserMultiFormatReader, BarcodeFormat } from '@zxing/browser';
import { DecodeHintType } from '@zxing/library';
import { useAuth, useBarcodeScanner } from '../../hooks/useApi';
import ApiService from '../../services/ApiService';
import SpinWheel from '../SpinWheel/SpinWheel';
import { theme, animations } from '../../styles/theme';

const glowPulse = keyframes`
  0% { filter: drop-shadow(0 0 15px rgba(255, 140, 0, 0.6)); }
  50% { filter: drop-shadow(0 0 25px rgba(255, 215, 0, 0.9)); }
  100% { filter: drop-shadow(0 0 15px rgba(255, 140, 0, 0.6)); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulseScale = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 20px rgba(255, 215, 0, 0.3), 0 0 40px rgba(255, 140, 0, 0.15); }
  50% { transform: scale(1.02); box-shadow: 0 0 30px rgba(255, 215, 0, 0.5), 0 0 60px rgba(255, 140, 0, 0.25); }
`;

const spinIcon = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const scanAnimation = keyframes`
  0% { top: 0%; opacity: 0; }
  50% { opacity: 1; }
  100% { top: 100%; opacity: 0; }
`;

const ScanLine = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 2px;
  background: #FFD700;
  box-shadow: 0 0 10px #FFD700;
  animation: ${scanAnimation} 2s linear infinite;
  z-index: 5;
`;

const SwitchUserLink = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition: color 0.2s ease;
  letter-spacing: 0.02em;

  &:hover {
    color: rgba(255, 255, 255, 0.6);
  }
`;
const sparkle = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;
const FullScreenLockOverlay = styled.div`
  position: relative;
  background: #050505;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 92px);
  padding: 2rem 1.5rem;
  overflow: hidden;

&::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 2% 4%, rgba(255, 255, 255, 0.26) 1px, transparent 0),
                      radial-gradient(circle at 29% 36%, rgba(255, 255, 255, 0.32) 1px, transparent 0),
                      radial-gradient(circle at 50% 60%, rgba(255, 255, 255, 0.2) 1px, transparent 0),
                      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.66) 1px, transparent 0),
                      radial-gradient(circle at 70% 10%, rgba(255, 255, 255, 0.11) 1px, transparent 0),
                      radial-gradient(circle at 60% 12%, rgba(255, 255, 255, 0.29) 1px, transparent 0),
                      radial-gradient(circle at 43% 90%, rgba(255, 255, 255, 0.51) 1px, transparent 0),
                      radial-gradient(circle at 32% 83%, rgba(255, 255, 255, 0.16) 1px, transparent 0),
                      radial-gradient(circle at 49% 53%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
                      radial-gradient(circle at 89% 52%, rgba(255, 255, 255, 0.14) 1px, transparent 0);

    
    background-size: 25% 25%;
    pointer-events: none;
    background-repeat: repeat;
    animation: ${sparkle} 4s ease-in-out infinite;

  }
`;

const GatewayWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
  position: relative;
  z-index: 10;
`;

const GatewayLogo = styled.img`
  width: 96px;
  height: 96px;
  margin-bottom: 1.75rem;
  animation: ${glowPulse} 4s ease-in-out infinite;
  object-fit: contain;
`;

const GatewayTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.5rem;
  text-align: center;
  letter-spacing: -0.02em;
`;

const GoldText = styled.span`
  color: #FFD700;
`;

const GatewayDesc = styled.p`
  color: rgba(255, 255, 255, 0.45);
  font-size: 1rem;
  text-align: center;
  margin: 0 0 2rem;
  line-height: 1.6;
  max-width: 340px;
`;

const ScanCard = styled.div`
  width: 100%;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  overflow: hidden;
`;

const ScanCardSection = styled.div`
  padding: 1.25rem;

  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.05);
  }
`;

const ScanCardLabel = styled.div`
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-weight: 600;
  margin-bottom: 0.85rem;
`;

const ManualRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const GatewayActions = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const SpinDrawerToggle = styled.button`
  width: 100%;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%);
  background-size: 200% auto;
  color: #000;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 1rem 1.25rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  animation: ${pulseScale} 2.5s ease-in-out infinite;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 2.5s ease-in-out infinite;
  }

  &:hover {
    filter: brightness(1.1);
    transform: scale(1.04);
    animation: none;
    box-shadow: 0 0 35px rgba(255, 215, 0, 0.6), 0 0 70px rgba(255, 140, 0, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SpinIconSvg = styled.span`
  display: inline-flex;
  font-size: 1.3rem;
  animation: ${spinIcon} 3s linear infinite;
  filter: drop-shadow(0 0 3px rgba(0, 0, 0, 0.3));
`;

const SpinDrawerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.62);
  opacity: ${props => props.$open ? 1 : 0};
  pointer-events: ${props => props.$open ? 'auto' : 'none'};
  transition: opacity 0.28s ease;
  z-index: 1150;
`;

const SpinDrawerPanel = styled.aside`
  position: fixed;
  top: 92px;
  right: 0;
  width: min(560px, 96vw);
  height: calc(100vh - 92px);
  background: rgba(6, 6, 6, 0.98);
  border-left: 1px solid rgba(255, 215, 0, 0.22);
  box-shadow: -18px 0 40px rgba(0, 0, 0, 0.5);
  transform: translateX(${props => props.$open ? '0' : '105%'});
  transition: transform 0.3s ease;
  z-index: 1160;
  padding: 0.9rem;
  overflow-y: auto;

  @media (max-width: 768px) {
    top: 80px;
    height: calc(100vh - 80px);
    width: 100vw;
    padding: 0.7rem;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.55rem;
  border-bottom: 1px solid rgba(255, 215, 0, 0.15);
  padding-bottom: 0.5rem;
`;

const DrawerTitle = styled.h3`
  margin: 0;
  color: #FFD700;
  font-size: 1.08rem;
  font-weight: 700;
  letter-spacing: 0.03em;
`;

const DrawerCloseButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #f0f0f0;
  border-radius: 8px;
  min-width: 34px;
  height: 34px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 215, 0, 0.45);
    color: #FFD700;
  }
`;



const WebcamContainer = styled.div`
  position: relative;
  display: inline-block;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.2);
  border: 2px solid rgba(255, 215, 0, 0.4);
`;

const ScannerContainer = styled.div`
  max-width: ${props => props.$fullScreen ? '900px' : '1400px'};
  margin: 0 auto;
  padding: ${props => props.$embedded ? '0' : (props.$fullScreen ? '2rem 1rem' : '3rem')};
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  width: 100%;
  /* Removed pointer-events: none; which prevented all interaction */

  @media (max-width: 1400px) {
    padding: ${props => props.$embedded ? '0' : '2rem clamp(1.5rem, 3vw, 2rem)'};
  }

  @media (max-width: 768px) {
    padding: ${props => props.$embedded ? '0' : '1.5rem 1rem'};
  }

  @media (max-width: 480px) {
    padding: ${props => props.$embedded ? '0' : '1rem 0.75rem'};
  }
`;

const ScannerWrapper = styled.div`
  min-height: ${props => props.$embedded ? 'auto' : (props.$fullScreen ? '100vh' : '100vh')};
  background: ${props => props.$embedded ? 'transparent' : theme.gradients.background};
  position: ${props => props.$embedded ? 'relative' : (props.$fullScreen ? 'fixed' : 'relative')};
  top: ${props => props.$fullScreen && !props.$embedded ? '0' : 'auto'};
  left: ${props => props.$fullScreen && !props.$embedded ? '0' : 'auto'};
  right: ${props => props.$fullScreen && !props.$embedded ? '0' : 'auto'};
  bottom: ${props => props.$fullScreen && !props.$embedded ? '0' : 'auto'};
  z-index: ${props => props.$fullScreen && !props.$embedded ? '9998' : 'auto'};
  overflow: ${props => props.$embedded ? 'visible' : 'hidden'};
  width: ${props => props.$embedded ? '100%' : 'auto'};
  margin-top: ${props => props.$embedded ? '2rem' : '0'};

  ${props => !props.$embedded && css`
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
      pointer-events: none;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: ${theme.colors.backgroundStars};
      background-size: 25% 25%;
      pointer-events: none;
      background-repeat: repeat;
      animation: ${animations.sparkle} 4s ease-in-out infinite;
    }
  `}
`;

const Header = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  color: white;
  padding: 2rem;
  border-radius: ${theme.borderRadius.large};
  margin-bottom: 2rem;
  text-align: center;
  box-sizing: border-box;
  box-shadow: ${theme.shadows.card};
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const Title = styled.h1`
  margin: 0 0 1rem 0;
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: clamp(0.9rem, 2vw, 1.2rem);
  opacity: 0.9;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ScannerSection = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  padding: 2rem;
  box-sizing: border-box;
  width: 100%;
  box-shadow: ${theme.shadows.card};
  margin-bottom: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 1.5rem 0;
  color: ${theme.colors.textPrimary};
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin: 1.5rem 0;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
    margin: 1rem 0;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !['primary', 'success', 'danger'].includes(prop),
})`
  padding: 0.75rem 2rem;
  background: ${props => 
    props.primary ? theme.gradients.primary :
    props.success ? theme.gradients.primary :
    props.danger ? 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)' :
    'linear-gradient(135deg, #333333 0%, #1a1a1a 100%)'
  };
  color: ${props => props.primary || props.success ? '#000' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: clamp(0.9rem, 2vw, 1rem);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  min-height: 44px;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
    padding: 0.875rem 1.5rem;
    font-size: 0.9rem;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const ManualInputSection = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  padding: 2rem;
  box-shadow: ${theme.shadows.card};
  margin-bottom: 2rem;
  
  p {
    color: ${theme.colors.textSecondary};
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  background: rgba(10, 10, 10, 0.5);
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: 8px;
  font-size: 1rem;
  width: 300px;
  color: ${theme.colors.textPrimary};
  transition: all 0.2s;

  @media (max-width: 600px) {
    width: 100%;
    box-sizing: border-box;
  }
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.cardBorderHover};
    box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.2);
  }
  
  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const FileUploadSection = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  padding: 2rem;
  box-shadow: ${theme.shadows.card};
  margin-bottom: 2rem;
  text-align: center;
  
  p {
    color: ${theme.colors.textSecondary};
  }
`;

const FileLabel = styled.label`
  display: inline-block;
  padding: 0.75rem 2rem;
  background: ${theme.gradients.primary};
  color: #000;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: ${theme.shadows.button};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const PointsInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
`;

const PointsBadge = styled.div`
  background: ${props => props.$success ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  border: 1px solid ${props => props.$success ? 'rgba(81, 207, 102, 0.3)' : 'rgba(220, 20, 60, 0.3)'};
  padding: 1rem;
  border-radius: 8px;
  font-weight: bold;
  color: ${props => props.success ? '#51cf66' : '#0dcc73ff'};
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Modal = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  animation: ${fadeIn} 0.3s ease-out;
  padding: ${theme.spacing.md};
  box-sizing: border-box;
  overflow-y: auto;

  @media (max-width: 480px) {
    padding: ${theme.spacing.sm};
    align-items: flex-start;
    padding-top: ${theme.spacing.xl};
  }
`;

const ModalContent = styled.div`
  background: ${theme.colors.cardBackgroundThick};
  position: absolute;
  top: 20%;
  left: 2.5%;
  right: 2.5%;
  
  border: 2px solid;
  border-image: ${theme.colors.cardBorder};
  border-image-slice: 1;
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.xxl};
  max-width: 500px;
  width: auto;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${theme.shadows.card};
  animation: ${fadeIn} 0.4s ease-out;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: ${theme.spacing.xl};
    width: auto;
    max-height: 85vh;
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.lg};
    width: auto;
    max-height: 90vh;
    border-radius: ${theme.borderRadius.medium};
  }
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.fontSizes.xxl};
  font-weight: ${theme.typography.fontWeights.bold};
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const ModalText = styled.p`
  font-size: ${theme.typography.fontSizes.md};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing.md};
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
`;

const BarcodeScanner = ({ onNavigate, requiresScan }) => {
  const { user, updateUserPoints, logout } = useAuth();
  const { clearResults } = useBarcodeScanner();
  const [manualBarcode, setManualBarcode] = useState('');
  const [scanningStatus, setScanningStatus] = useState('');
  const [showModal, setShowModal] = useState(false);


  

  const [isScanning, setIsScanning] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const videoRef = useRef(null);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [framesTried, setFramesTried] = useState(0);
  const [isSpinDrawerOpen, setIsSpinDrawerOpen] = useState(false);
  const readerRef = useRef(null);
  const streamRef = useRef(null);
  const controlsRef = useRef(null);

  // Initialize reader with all common formats and TRY_HARDER hint
  useEffect(() => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.ITF,
      BarcodeFormat.CODE_93,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.AZTEC
    ]);
    hints.set(DecodeHintType.TRY_HARDER, true);
    readerRef.current = new BrowserMultiFormatReader(hints);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop camera stream if active
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startScanning = async () => {
    if (isScanning) return;
    setIsScanning(true);
    setError('');
    setResult('');
    setFramesTried(0);
    setScanningStatus('Initializing camera...');

    try {
      // Enumerate devices first
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(d => d.kind === 'videoinput');
      setAvailableDevices(videoInputs);
      
      let chosen = selectedDeviceId;
      if (!chosen) {
        // Prefer back/environment camera
        const preferred = videoInputs.find(d => /back|rear|environment/i.test(d.label)) || videoInputs[0];
        chosen = preferred ? preferred.deviceId : undefined;
        setSelectedDeviceId(chosen);
      }

      const videoElement = videoRef.current;
      if (!videoElement) throw new Error('Video element missing');

      // Use ZXing's built-in decodeFromVideoDevice which handles the loop efficiently
      const controls = await readerRef.current.decodeFromVideoDevice(
        chosen,
        videoElement,
        (result, err) => {
          if (result) {
            const text = result.text || (result.getText && result.getText());
            handleScanResult(text);
            setScanningStatus('Code detected');
            stopScanning();
          }
          if (err && err.name !== 'NotFoundException') {
             // Only log real errors, not "no code found"
             // setDebugInfo(err.message); 
          }
          setFramesTried(prev => prev + 1);
        }
      );
      controlsRef.current = controls;
      
      setScanningStatus('Scanning... hold code steady');
      
      // Capture stream for cleanup
      if (videoElement.srcObject) {
        streamRef.current = videoElement.srcObject;
      }

    } catch (e) {
      console.error('Camera setup failed:', e);
      setError(e.message || 'Unable to access camera');
      setIsScanning(false);
      setScanningStatus('');
    }
  };
 
  const stopScanning = () => {
    try {
      // Stop the continuous decode using controls
      if (controlsRef.current) {
        controlsRef.current.stop();
        controlsRef.current = null;
      }
      
      // Stop video tracks
      const videoElement = videoRef.current;
      if (videoElement && videoElement.srcObject) {
        const tracks = videoElement.srcObject.getTracks();
        tracks.forEach(t => t.stop());
        videoElement.srcObject = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    } catch (error) {
      console.error('Error stopping scanner:', error);
    } finally {
      setIsScanning(false);
      setScanningStatus('');
      setFramesTried(0);
    }
  };


  // const stopScanning = () => {
  //   console.log('Stopping scanner...');
    
  //   try {
  //     // Clear scanning interval
     
      
  //     // The reader will be reused for next scan
  //     // No need to reset as we control the scanning loop manually
      
  //     // Stop camera stream
  //     readerRef.current.reset();
  //     if (streamRef.current) {
  //       streamRef.current.getTracks().forEach(track => {
  //         track.stop();
  //       });
  //       streamRef.current = null;
  //     }
      
  //     // Clear video source
  //     const videoElement = webcamRef.current?.video;
  //     if (videoElement) {
  //       videoElement.srcObject = null;
  //     }
  //   } catch (error) {
  //     console.error('Error stopping scanner:', error);
  //   } finally {
  //     setIsScanning(false);
  //     setScanningStatus('Scanner stopped');
  //   }
  // };

   const handleFileUpload = async (event) => {
     const file = event.target.files[0];
     if (!file) return;
     setError('');
     setResult('');
     const img = new Image();
     img.onload = async () => {
       try {
         const result = await readerRef.current.decodeFromImageElement(img);
         const text = result.text || (result.getText && result.getText());
         handleScanResult(text);
       } catch (err) {
         console.error('Image decode error:', err);
         setError('Could not read barcode from image');
       }
     };
     img.onerror = () => setError('Failed to load image');
     img.src = URL.createObjectURL(file);
   };
 
   const handleScanResult = async (barcode) => {
     if (!barcode) return;
     try {
       console.log('Scanned barcode:', barcode);
        if (!user) {
         setError('User not authenticated');
         return;
       }
        const response = await ApiService.scanBarcode(barcode, user.id);
       if (!response) return;
       setResult({
         success: response.success,
         message: response.message,
         pointsChange: response.pointsChange,
         newTotalPoints: response.newTotalPoints,
         unlockedFrame: response.unlockedFrame || null,
       });

       // Check if this was the first scan that unlocked the app
       if (response.data?.isFirstScan || response.isFirstScan) {
         console.log('🎉 First scan! App access unlocked');
         // Reload the page to refresh scan status and grant access
         setTimeout(() => {
           window.location.reload();
         }, 2000);
       }

       const updatedUser = { ...user, points: response.newTotalPoints };
       localStorage.setItem('user', JSON.stringify(updatedUser));
       updateUserPoints(response.newTotalPoints);
       setShowModal(true);
     } catch (error) {
       console.error('Scan error:', error);
       setError(error.message || error.response?.data?.message || 'Failed to process scan');
       setShowModal(true);
     }
   };
  //  const handleScanResult = async (barcode) => {
  //    try {
  //      const response = await ApiService.scanBarcode(barcode, user.id, token);
       
  //      setResult(`Product scanned successfully! Barcode: ${barcode}`);
  //      setPoints(response.data.pointsEarned);
       
  //      // Update user points in localStorage
  //      const updatedUser = { ...user, points: (user.points || 0) + response.data.pointsEarned };
  //      localStorage.setItem('user', JSON.stringify(updatedUser));
       
  //    } catch (error) {
  //      setError(error.response?.data?.message || 'Failed to process scan');
  //    }
  //  };

   const handleManualScan = async () => {
     if (!manualBarcode.trim()) return;
     setError('');
     setResult('');
      try {
        const response = await ApiService.scanBarcode(manualBarcode.trim(), user.id);
        if (!response) return;
        setResult({
          success: response.success,
          message: response.message,
          pointsChange: response.pointsChange,
          newTotalPoints: response.newTotalPoints,
          unlockedFrame: response.unlockedFrame || null,
        });

        // Check if this was the first scan that unlocked the app
        if (response.data?.isFirstScan || response.isFirstScan) {
          console.log('🎉 First scan! App access unlocked');
          // Reload the page to refresh scan status and grant access
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
        
        // Update user points in localStorage
        const updatedUser = { ...user, points: response.newTotalPoints };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        updateUserPoints(response.newTotalPoints);
        setManualBarcode(''); // Clear the input after successful scan
        setShowModal(true);
      } catch (error) {
        console.error('Manual scan error:', error);
        setError(error.message || error.response?.data?.message || 'Failed to process scan');
        setShowModal(true);
      }
    };

   const clearAllResults = () => {
     setResult('');
     setError('');
     clearResults();
     setShowModal(false);
   };

  const walletBalance = Number.isFinite(Number(user?.points)) ? Number(user?.points) : 0;

  // Full screen lock view when scan is required
  if (requiresScan) {
    return (
      <>
        <FullScreenLockOverlay>
          <GatewayWrap>
            <GatewayLogo src="images/GGlogo.svg" alt="GG Trophy" />

            <GatewayTitle>
              <GoldText>GG</GoldText> Trophy
            </GatewayTitle>

            <GatewayDesc>
              Scan any valid code to unlock your account.
            </GatewayDesc>

            <ScanCard>
              <ScanCardSection>
                <ScanCardLabel>Camera</ScanCardLabel>
                {isScanning && (
                  <WebcamContainer style={{ width: '100%', height: '200px', marginBottom: '0.75rem' }}>
                    <video
                      ref={videoRef}
                      style={{ width: '100%', height: '100%', background: '#000', borderRadius: 10, objectFit: 'cover' }}
                      muted
                      playsInline
                    />
                    <ScanLine />
                  </WebcamContainer>
                )}
                {!isScanning && availableDevices.length > 1 && (
                  <div style={{ marginBottom: '0.65rem' }}>
                    <select
                      value={selectedDeviceId || ''}
                      onChange={(e) => setSelectedDeviceId(e.target.value || null)}
                      style={{ background: '#111', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '0.55rem 0.75rem', width: '100%', fontSize: '0.9rem' }}
                    >
                      {availableDevices.map(d => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || `Camera ${d.deviceId.substring(0, 8)}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {!isScanning ? (
                  <Button primary onClick={startScanning} style={{ width: '100%' }}>
                    Open Camera
                  </Button>
                ) : (
                  <Button danger onClick={stopScanning} style={{ width: '100%' }}>
                    Stop Camera
                  </Button>
                )}
              </ScanCardSection>

              <ScanCardSection>
                <ScanCardLabel>Manual Entry</ScanCardLabel>
                <ManualRow>
                  <Input
                    type="text"
                    placeholder="Enter code..."
                    value={manualBarcode}
                    onChange={(e) => setManualBarcode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleManualScan()}
                    style={{ flex: 1, width: 'auto' }}
                  />
                  <Button success onClick={handleManualScan} disabled={!manualBarcode.trim()} style={{ minWidth: '90px' }}>
                    Submit
                  </Button>
                </ManualRow>
              </ScanCardSection>
            </ScanCard>

            <GatewayActions>
              <SpinDrawerToggle onClick={() => setIsSpinDrawerOpen(true)}>
                <SpinIconSvg>🎡</SpinIconSvg>
                Spin & Win
              </SpinDrawerToggle>

              <SwitchUserLink onClick={() => {
                logout();
                if (onNavigate) {
                  onNavigate('login');
                } else {
                  window.location.reload();
                }
              }}>
                Switch User
              </SwitchUserLink>
            </GatewayActions>
          </GatewayWrap>

          {showModal && (result || error) && (
            <Modal onClick={clearAllResults}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalTitle>
                  {result?.success ? 'Success' : 'Scan Error'}
                </ModalTitle>
                {error && <ModalText style={{ color: '#ff6b6b' }}>{error}</ModalText>}
                {result && (
                  <>
                    <ModalText>
                      {result.message || 'Scan processed successfully'}
                    </ModalText>
                    {result.success && result.unlockedFrame && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 14px',
                        background: 'rgba(255,215,0,0.07)',
                        border: '1px solid rgba(255,215,0,0.3)',
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: result.unlockedFrame,
                          border: '3px solid rgba(255,255,255,0.35)',
                          flexShrink: 0,
                          boxShadow: `0 0 10px ${result.unlockedFrame}88`,
                        }} />
                        <div>
                          <div style={{ color: 'rgba(255,215,0,0.9)', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Frame Unlocked!</div>
                          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.73rem', marginTop: '2px' }}>Available in Profile → Customize</div>
                        </div>
                      </div>
                    )}
                    {result.success && result.pointsChange !== undefined && (
                      <PointsInfo>
                        <PointsBadge $success={result.pointsChange > 0}>
                          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Change</div>
                          <div style={{ fontSize: '1.5rem', marginTop: '0.25rem' }}>
                            {result.pointsChange > 0 ? '+' : ''}{result.pointsChange}
                          </div>
                        </PointsBadge>
                        <PointsBadge $success={true}>
                          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>New Balance</div>
                          <div style={{ fontSize: '1.5rem', marginTop: '0.25rem' }}>
                            {result.newTotalPoints?.toLocaleString() || 0}
                          </div>
                        </PointsBadge>
                      </PointsInfo>
                    )}
                  </>
                )}
                <ButtonGroup>
                  <Button primary onClick={clearAllResults} style={{ flex: 1 }}>
                    Close
                  </Button>
                </ButtonGroup>
              </ModalContent>
            </Modal>
          )}
        </FullScreenLockOverlay>

        <SpinDrawerBackdrop $open={isSpinDrawerOpen} onClick={() => setIsSpinDrawerOpen(false)} />
        <SpinDrawerPanel $open={isSpinDrawerOpen}>
          <DrawerHeader>
            <DrawerTitle>Spin Wheel</DrawerTitle>
            <DrawerCloseButton onClick={() => setIsSpinDrawerOpen(false)} aria-label="Close spin wheel drawer">
              ×
            </DrawerCloseButton>
          </DrawerHeader>
          <SpinWheel
            embedded={true}
            compact={false}
            minimalText={true}
            onNavigate={onNavigate}
            user={user}
            updateUserPoints={updateUserPoints}
          />
        </SpinDrawerPanel>
      </>
    );
  }

  // Normal scanner view when scan is not required
  return (
    <ScannerWrapper $fullScreen={false}>
      <ScannerContainer $fullScreen={false}>
      <Header>
        <Title>📱 Barcode Scanner</Title>
        <Subtitle>Scan products, earn rewards, unlock prizes!</Subtitle>
      </Header>

      <ScannerSection>
        <SectionTitle>📷 Camera Scan</SectionTitle>
        {isScanning && (
          <WebcamContainer>
            <video
              ref={videoRef}
              style={{ width: 300, height: 300, background: 'transparent', borderRadius: 12 }}
              muted
              playsInline
            />
            {/* <div style={{position:'absolute',left:0,top:0,right:0,bottom:0,border:'2px solid rgba(255,215,0,0.4)',pointerEvents:'none'}} /> */}
          </WebcamContainer>
        )}
        {isScanning && (
          <div style={{fontSize:'0.8rem',opacity:0.8}}>
            Frames tried: {framesTried}
          </div>
        )}
        {!isScanning && availableDevices.length > 1 && (
          <div style={{marginTop:'0.5rem'}}>
            <label style={{marginRight:'0.5rem',color:'rgba(255,255,255,0.7)',fontSize:'0.9rem'}}>Camera:</label>
            <select
              value={selectedDeviceId || ''}
              onChange={(e)=>setSelectedDeviceId(e.target.value || null)}
              style={{background:'#111',color:'#fff',border:'1px solid #444',borderRadius:6,padding:'0.4rem'}}
            >
              {availableDevices.map(d => (
                <option key={d.deviceId} value={d.deviceId}>{d.label || 'Camera'}</option>
              ))}
            </select>
          </div>
        )}

        {scanningStatus && (
          <div style={{
            margin: '1rem 0',
            padding: '1rem',
            background: 'rgba(255, 215, 0, 0.15)',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            borderRadius: '8px',
            color: '#FFD700',
            fontWeight: '500'
          }}>
            {scanningStatus}
          </div>
        )}

        <ButtonContainer>
          {!isScanning ? (
            <Button primary onClick={startScanning}>
              📷 Start Camera Scan
            </Button>
          ) : (
            <Button danger onClick={stopScanning}>
              ⏹️ Stop Scanning
            </Button>
          )}
        </ButtonContainer>
      </ScannerSection>

      <FileUploadSection>
        <SectionTitle>🖼️ Upload Image</SectionTitle>
        <p>Have a barcode image? Upload it here!</p>
        <FileLabel htmlFor="file-upload">
          📎 Choose Image File
        </FileLabel>
        <FileInput
          id="file-upload"
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
        />
      </FileUploadSection>

      <ManualInputSection>
        <SectionTitle>⌨️ Manual Entry</SectionTitle>
        <p>Know the barcode number? Enter it directly!</p>
        <InputGroup>
          <Input
            type="text"
            placeholder="Enter barcode number..."
            value={manualBarcode}
            onChange={(e) => setManualBarcode(e.target.value)}
          />
          <Button success onClick={handleManualScan} disabled={!manualBarcode.trim()}>
            ✓ Scan Code
          </Button>
        </InputGroup>
      </ManualInputSection>

      {showModal && (result || error) && (
        <Modal onClick={clearAllResults}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {result?.success ? '🎉 Success!' : '❌ Scan Error'}
            </ModalTitle>
            
            {result?.success ? (
              <>
                <ModalText>{result.message}</ModalText>
                {result.unlockedFrame && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 14px',
                    background: 'rgba(255,215,0,0.07)',
                    border: '1px solid rgba(255,215,0,0.3)',
                    borderRadius: '8px',
                    marginBottom: '12px',
                  }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: result.unlockedFrame,
                      border: '3px solid rgba(255,255,255,0.35)',
                      flexShrink: 0,
                      boxShadow: `0 0 10px ${result.unlockedFrame}88`,
                    }} />
                    <div>
                      <div style={{ color: 'rgba(255,215,0,0.9)', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Frame Unlocked!</div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.73rem', marginTop: '2px' }}>Available in Profile → Customize</div>
                    </div>
                  </div>
                )}
                <PointsInfo>
                  <PointsBadge $success={true}>
                    <div>Earned</div>
                    <div style={{fontSize: '1.5rem', marginTop: '0.5rem'}}>+{result.pointsChange}</div>
                  </PointsBadge>
                  <PointsBadge $success={true}>
                    <div>Balance</div>
                    <div style={{fontSize: '1.5rem', marginTop: '0.5rem'}}>{result.newTotalPoints}</div>
                  </PointsBadge>
                </PointsInfo>
              </>
            ) : (
              <ModalText style={{ color: theme.colors.error }}>
                {error || result?.message || 'An unknown error occurred'}
              </ModalText>
            )}

            <ButtonGroup>
              <Button primary onClick={clearAllResults}>
                {result?.success ? 'Awesome!' : 'Try Again'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
      </ScannerContainer>
    </ScannerWrapper>
  );
};

export default BarcodeScanner;